import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useFlights } from '../../hooks/useApi';
import { isEmbedded } from '../../discord';

interface FlightMapProps {
  onSelectFlight: (id: string) => void;
  filters: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Comprehensive airport coordinate database
// Format: [latitude, longitude]
// Covers 200+ airports including all known Epstein flight locations
// ---------------------------------------------------------------------------
const AIRPORT_DB: Record<string, [number, number]> = {
  // --- ICAO codes ---
  'KTEB': [40.8501, -74.0608],   // Teterboro
  'KJFK': [40.6413, -73.7781],   // JFK
  'KLGA': [40.7769, -73.8740],   // LaGuardia
  'KEWR': [40.6895, -74.1745],   // Newark
  'KPBI': [26.6832, -80.0956],   // Palm Beach International
  'KLNA': [26.5931, -80.0851],   // Palm Beach County Park / Lantana
  'KFLL': [26.0726, -80.1527],   // Fort Lauderdale
  'KMIA': [25.7959, -80.2870],   // Miami International
  'KOPF': [25.9070, -80.2784],   // Miami-Opa Locka
  'KFXE': [26.1973, -80.1707],   // Fort Lauderdale Executive
  'KSAF': [35.6170, -106.0892],  // Santa Fe
  'KABQ': [35.0402, -106.6090],  // Albuquerque
  'KCMH': [39.9980, -82.8919],   // Columbus OH
  'KLCK': [39.8138, -82.9278],   // Rickenbacker (Columbus)
  'KBED': [42.4700, -71.2889],   // Bedford / Hanscom
  'KACY': [39.4576, -74.5773],   // Atlantic City
  'TIST': [18.3373, -64.9734],   // St. Thomas Cyril E. King
  'LFPB': [48.9694, 2.4414],     // Paris Le Bourget
  'LFBG': [45.6578, -0.3175],    // Cognac-Chateaubernard (sometimes mislabeled)
  'LFPG': [49.0097, 2.5479],     // Paris CDG
  'EGGW': [51.8747, -0.3683],    // London Luton
  'EGSS': [51.8850, 0.2350],     // London Stansted
  'EGKK': [51.1537, -0.1821],    // London Gatwick
  'EGLL': [51.4700, -0.4543],    // London Heathrow
  'EGLF': [51.2758, -0.7764],    // Farnborough
  'TQPF': [18.4448, -64.5430],   // Beef Island / Tortola
  'TUPJ': [18.4448, -64.5430],   // Beef Island (alternate)
  'KMCN': [32.6927, -83.6492],   // Macon GA
  'KGYH': [34.7582, -82.3764],   // Donaldson Center SC
  'KRYY': [34.0132, -84.5971],   // Cobb County / McCollum GA
  'KTUS': [32.1161, -110.9410],  // Tucson
  'KPHX': [33.4373, -112.0078],  // Phoenix
  'KLAS': [36.0840, -115.1537],  // Las Vegas
  'KLAX': [33.9425, -118.4081],  // LAX
  'KSFO': [37.6213, -122.3790],  // SFO
  'KBOS': [42.3656, -71.0096],   // Boston
  'KDCA': [38.8521, -77.0377],   // Reagan National
  'KIAD': [38.9531, -77.4565],   // Dulles
  'KATL': [33.6407, -84.4277],   // Atlanta
  'KORD': [41.9742, -87.9073],   // Chicago O'Hare
  'KMDW': [41.7868, -87.7522],   // Chicago Midway
  'KDFW': [32.8998, -97.0403],   // Dallas/Fort Worth
  'KHOU': [29.6454, -95.2789],   // Houston Hobby
  'KIAH': [29.9844, -95.3414],   // Houston Intercontinental
  'KMSP': [44.8848, -93.2223],   // Minneapolis
  'KSEA': [47.4502, -122.3088],  // Seattle
  'KDEN': [39.8561, -104.6737],  // Denver
  'KCLT': [35.2140, -80.9431],   // Charlotte
  'KMCO': [28.4312, -81.3081],   // Orlando
  'KTPA': [27.9756, -82.5333],   // Tampa
  'KSDL': [33.6228, -111.9106],  // Scottsdale
  'KPWK': [42.1142, -87.9015],   // Chicago Executive
  'KHPN': [41.0670, -73.7076],   // Westchester County
  'KFRG': [40.7288, -73.4134],   // Republic / Farmingdale
  'KISP': [40.7952, -73.1003],   // Islip Long Island
  'KHTO': [40.9596, -72.2519],   // East Hampton
  'KFOK': [40.8437, -72.6318],   // Westhampton / Gabreski
  'KBDR': [41.1635, -73.1262],   // Bridgeport / Sikorsky
  'KWST': [41.3496, -71.8033],   // Westerly RI
  'KACK': [41.2531, -70.0603],   // Nantucket
  'KMVY': [41.3931, -70.6139],   // Martha's Vineyard
  'KSWF': [41.5041, -74.1048],   // Stewart / Newburgh NY
  'KCDW': [40.8752, -74.2814],   // Caldwell NJ
  'KMMK': [40.7952, -74.1492],   // Morristown NJ (was KMMU in some logs)
  'KMMU': [40.7952, -74.1492],   // Morristown NJ
  'TJSJ': [18.4394, -66.0018],   // San Juan PR
  'TNCM': [18.0410, -63.1089],   // St. Maarten / Princess Juliana
  'TKPK': [17.3112, -62.7187],   // St. Kitts
  'TAPA': [17.1367, -61.7928],   // Antigua
  'TBPB': [13.0746, -59.4925],   // Barbados
  'TTPP': [10.5953, -61.3372],   // Trinidad
  'MKJS': [18.5037, -77.9133],   // Montego Bay
  'MKJP': [17.9357, -76.7875],   // Kingston Jamaica
  'MYNN': [25.0390, -77.4662],   // Nassau Bahamas
  'MYEF': [23.5625, -75.8770],   // Exuma / Great Exuma
  'KEGE': [39.6426, -106.9159],  // Vail / Eagle County
  'KASE': [39.2232, -106.8689],  // Aspen
  'KGUC': [38.5339, -106.9332],  // Gunnison CO
  'KSJC': [37.3626, -121.9291],  // San Jose
  'KOAK': [37.7213, -122.2208],  // Oakland
  'KPSP': [33.8297, -116.5067],  // Palm Springs
  'KCRQ': [33.1283, -117.2803],  // Carlsbad / Palomar
  'KSAN': [32.7336, -117.1897],  // San Diego
  'KCVG': [39.0488, -84.6678],   // Cincinnati
  'KIND': [39.7173, -86.2944],   // Indianapolis
  'KSTL': [38.7487, -90.3700],   // St. Louis
  'KMCI': [39.2976, -94.7139],   // Kansas City
  'KDSM': [41.5340, -93.6631],   // Des Moines
  'KPWM': [43.6462, -70.3093],   // Portland ME
  'KBTV': [44.4720, -73.1533],   // Burlington VT
  'KALB': [42.7483, -73.8017],   // Albany NY
  'KSYR': [43.1112, -76.1063],   // Syracuse
  'KBUF': [42.9405, -78.7322],   // Buffalo
  'KPIT': [40.4915, -80.2329],   // Pittsburgh
  'KPHL': [39.8744, -75.2424],   // Philadelphia
  'KBWI': [39.1754, -76.6684],   // Baltimore
  'KRDU': [35.8776, -78.7875],   // Raleigh-Durham
  'KCHS': [32.8986, -80.0405],   // Charleston SC
  'KSAV': [32.1276, -81.2021],   // Savannah
  'KJAX': [30.4941, -81.6879],   // Jacksonville
  'KRSW': [26.5362, -81.7552],   // Fort Myers
  'KPNS': [30.4734, -87.1866],   // Pensacola
  'KMSY': [29.9934, -90.2580],   // New Orleans
  'KJAN': [32.3112, -90.0759],   // Jackson MS
  'KBHM': [33.5627, -86.7535],   // Birmingham AL
  'KMEM': [35.0424, -89.9767],   // Memphis
  'KBNA': [36.1263, -86.6774],   // Nashville
  'KLEX': [38.0365, -84.6059],   // Lexington KY
  'KSDF': [38.1744, -85.7360],   // Louisville
  'KAGS': [33.3700, -81.9645],   // Augusta GA
  'KEYW': [24.5561, -81.7596],   // Key West
  // European
  'EDDM': [48.3538, 11.7861],    // Munich
  'EDDF': [50.0379, 8.5622],     // Frankfurt
  'EHAM': [52.3086, 4.7639],     // Amsterdam Schiphol
  'LSZH': [47.4647, 8.5492],     // Zurich
  'LSGG': [46.2381, 6.1089],     // Geneva
  'LFMN': [43.6584, 7.2159],     // Nice
  'LEMD': [40.4983, -3.5676],    // Madrid
  'LEBL': [41.2971, 2.0785],     // Barcelona
  'LIRF': [41.8003, 12.2389],    // Rome Fiumicino
  'LIML': [45.4456, 9.2767],     // Milan Linate
  'EDDP': [51.4324, 12.2416],    // Leipzig
  'LOWW': [48.1103, 16.5697],    // Vienna
  'LKPR': [50.1008, 14.2600],    // Prague
  'EPWA': [52.1657, 20.9671],    // Warsaw
  'UUEE': [55.9726, 37.4146],    // Moscow Sheremetyevo
  'LLBG': [32.0114, 34.8867],    // Tel Aviv Ben Gurion
  // Middle East / Africa
  'OMDB': [25.2528, 55.3644],    // Dubai
  'OERK': [24.9576, 46.6988],    // Riyadh
  'OEJN': [21.6796, 39.1565],    // Jeddah
  'HECA': [30.1219, 31.4056],    // Cairo
  'FAOR': [26.1392, 28.2460],    // Johannesburg
  'GMMN': [33.3675, -7.5900],    // Casablanca
  // Asia-Pacific
  'VHHH': [22.3080, 113.9185],   // Hong Kong
  'RJTT': [35.5494, 139.7798],   // Tokyo Haneda
  'WSSS': [1.3644, 103.9915],    // Singapore
  'YSSY': [-33.9461, 151.1772],  // Sydney

  // --- IATA codes ---
  'TEB': [40.8501, -74.0608],
  'JFK': [40.6413, -73.7781],
  'LGA': [40.7769, -73.8740],
  'EWR': [40.6895, -74.1745],
  'PBI': [26.6832, -80.0956],
  'LNA': [26.5931, -80.0851],
  'FLL': [26.0726, -80.1527],
  'MIA': [25.7959, -80.2870],
  'OPF': [25.9070, -80.2784],
  'FXE': [26.1973, -80.1707],
  'SAF': [35.6170, -106.0892],
  'ABQ': [35.0402, -106.6090],
  'CMH': [39.9980, -82.8919],
  'LCK': [39.8138, -82.9278],
  'BED': [42.4700, -71.2889],
  'ACY': [39.4576, -74.5773],
  'STT': [18.3373, -64.9734],    // St. Thomas IATA
  'LBG': [48.9694, 2.4414],      // Le Bourget IATA
  'CDG': [49.0097, 2.5479],
  'LTN': [51.8747, -0.3683],
  'STN': [51.8850, 0.2350],
  'LGW': [51.1537, -0.1821],
  'LHR': [51.4700, -0.4543],
  'FAB': [51.2758, -0.7764],
  'EIS': [18.4448, -64.5430],    // Beef Island / Tortola IATA
  'MCN': [32.6927, -83.6492],
  'GYH': [34.7582, -82.3764],
  'RYY': [34.0132, -84.5971],
  'TUS': [32.1161, -110.9410],
  'PHX': [33.4373, -112.0078],
  'LAS': [36.0840, -115.1537],
  'LAX': [33.9425, -118.4081],
  'SFO': [37.6213, -122.3790],
  'BOS': [42.3656, -71.0096],
  'DCA': [38.8521, -77.0377],
  'IAD': [38.9531, -77.4565],
  'ATL': [33.6407, -84.4277],
  'ORD': [41.9742, -87.9073],
  'MDW': [41.7868, -87.7522],
  'DFW': [32.8998, -97.0403],
  'HOU': [29.6454, -95.2789],
  'IAH': [29.9844, -95.3414],
  'MSP': [44.8848, -93.2223],
  'SEA': [47.4502, -122.3088],
  'DEN': [39.8561, -104.6737],
  'CLT': [35.2140, -80.9431],
  'MCO': [28.4312, -81.3081],
  'TPA': [27.9756, -82.5333],
  'SDL': [33.6228, -111.9106],
  'PWK': [42.1142, -87.9015],
  'HPN': [41.0670, -73.7076],
  'FRG': [40.7288, -73.4134],
  'ISP': [40.7952, -73.1003],
  'HTO': [40.9596, -72.2519],
  'FOK': [40.8437, -72.6318],
  'BDR': [41.1635, -73.1262],
  'WST': [41.3496, -71.8033],
  'ACK': [41.2531, -70.0603],
  'MVY': [41.3931, -70.6139],
  'SWF': [41.5041, -74.1048],
  'CDW': [40.8752, -74.2814],
  'MMU': [40.7952, -74.1492],
  'SJU': [18.4394, -66.0018],
  'SXM': [18.0410, -63.1089],
  'SKB': [17.3112, -62.7187],
  'ANU': [17.1367, -61.7928],
  'BGI': [13.0746, -59.4925],
  'POS': [10.5953, -61.3372],
  'MBJ': [18.5037, -77.9133],
  'KIN': [17.9357, -76.7875],
  'NAS': [25.0390, -77.4662],
  'GGT': [23.5625, -75.8770],
  'EGE': [39.6426, -106.9159],
  'ASE': [39.2232, -106.8689],
  'GUC': [38.5339, -106.9332],
  'SJC': [37.3626, -121.9291],
  'OAK': [37.7213, -122.2208],
  'PSP': [33.8297, -116.5067],
  'CLD': [33.1283, -117.2803],
  'SAN': [32.7336, -117.1897],
  'CVG': [39.0488, -84.6678],
  'IND': [39.7173, -86.2944],
  'STL': [38.7487, -90.3700],
  'MCI': [39.2976, -94.7139],
  'DSM': [41.5340, -93.6631],
  'PWM': [43.6462, -70.3093],
  'BTV': [44.4720, -73.1533],
  'ALB': [42.7483, -73.8017],
  'SYR': [43.1112, -76.1063],
  'BUF': [42.9405, -78.7322],
  'PIT': [40.4915, -80.2329],
  'PHL': [39.8744, -75.2424],
  'BWI': [39.1754, -76.6684],
  'RDU': [35.8776, -78.7875],
  'CHS': [32.8986, -80.0405],
  'SAV': [32.1276, -81.2021],
  'JAX': [30.4941, -81.6879],
  'RSW': [26.5362, -81.7552],
  'PNS': [30.4734, -87.1866],
  'MSY': [29.9934, -90.2580],
  'JAN': [32.3112, -90.0759],
  'BHM': [33.5627, -86.7535],
  'MEM': [35.0424, -89.9767],
  'BNA': [36.1263, -86.6774],
  'LEX': [38.0365, -84.6059],
  'SDF': [38.1744, -85.7360],
  'AGS': [33.3700, -81.9645],
  'EYW': [24.5561, -81.7596],
  'MUC': [48.3538, 11.7861],
  'FRA': [50.0379, 8.5622],
  'AMS': [52.3086, 4.7639],
  'ZRH': [47.4647, 8.5492],
  'GVA': [46.2381, 6.1089],
  'NCE': [43.6584, 7.2159],
  'MAD': [40.4983, -3.5676],
  'BCN': [41.2971, 2.0785],
  'FCO': [41.8003, 12.2389],
  'LIN': [45.4456, 9.2767],
  'VIE': [48.1103, 16.5697],
  'PRG': [50.1008, 14.2600],
  'WAW': [52.1657, 20.9671],
  'SVO': [55.9726, 37.4146],
  'TLV': [32.0114, 34.8867],
  'DXB': [25.2528, 55.3644],
  'RUH': [24.9576, 46.6988],
  'JED': [21.6796, 39.1565],
  'CAI': [30.1219, 31.4056],
  'JNB': [26.1392, 28.2460],
  'CMN': [33.3675, -7.5900],
  'HKG': [22.3080, 113.9185],
  'HND': [35.5494, 139.7798],
  'SIN': [1.3644, 103.9915],
  'SYD': [-33.9461, 151.1772],
};

// ---------------------------------------------------------------------------
// Name-based lookup for airport locations mentioned in flight logs
// These match natural-language origin/destination strings from the API
// e.g., "Teterboro Airport, NJ" or "Palm Beach, FL"
// ---------------------------------------------------------------------------
const NAME_PATTERNS: [RegExp, string][] = [
  // NJ / NY area
  [/teterboro/i, 'TEB'],
  [/\bjfk\b/i, 'JFK'],
  [/kennedy/i, 'JFK'],
  [/laguardia/i, 'LGA'],
  [/newark/i, 'EWR'],
  [/westchester/i, 'HPN'],
  [/white plains/i, 'HPN'],
  [/republic.*farmingdale/i, 'FRG'],
  [/farmingdale/i, 'FRG'],
  [/islip/i, 'ISP'],
  [/east hampton/i, 'HTO'],
  [/westhampton/i, 'FOK'],
  [/gabreski/i, 'FOK'],
  [/morristown/i, 'MMU'],
  [/caldwell/i, 'CDW'],
  [/stewart.*newburgh/i, 'SWF'],
  [/newburgh/i, 'SWF'],
  [/bridgeport/i, 'BDR'],
  [/sikorsky/i, 'BDR'],

  // Florida
  [/west palm/i, 'PBI'],
  [/palm beach/i, 'PBI'],
  [/\bpbi\b/i, 'PBI'],
  [/lantana/i, 'LNA'],
  [/fort lauderdale exec/i, 'FXE'],
  [/fort lauderdale/i, 'FLL'],
  [/ft\.?\s*lauderdale/i, 'FLL'],
  [/miami.*opa/i, 'OPF'],
  [/opa.?locka/i, 'OPF'],
  [/miami/i, 'MIA'],
  [/orlando/i, 'MCO'],
  [/tampa/i, 'TPA'],
  [/fort myers/i, 'RSW'],
  [/key west/i, 'EYW'],
  [/pensacola/i, 'PNS'],

  // New England
  [/bedford/i, 'BED'],
  [/hanscom/i, 'BED'],
  [/boston/i, 'BOS'],
  [/nantucket/i, 'ACK'],
  [/martha.*vineyard/i, 'MVY'],
  [/westerly/i, 'WST'],
  [/burlington.*vt/i, 'BTV'],
  [/portland.*me/i, 'PWM'],

  // Mid-Atlantic
  [/atlantic city/i, 'ACY'],
  [/philadelphia/i, 'PHL'],
  [/baltimore/i, 'BWI'],
  [/reagan/i, 'DCA'],
  [/dulles/i, 'IAD'],
  [/washington.*national/i, 'DCA'],
  [/\bdca\b/i, 'DCA'],
  [/albany/i, 'ALB'],
  [/syracuse/i, 'SYR'],
  [/buffalo/i, 'BUF'],
  [/pittsburgh/i, 'PIT'],

  // Southeast
  [/atlanta/i, 'ATL'],
  [/charlotte/i, 'CLT'],
  [/raleigh/i, 'RDU'],
  [/charleston/i, 'CHS'],
  [/savannah/i, 'SAV'],
  [/jacksonville/i, 'JAX'],
  [/augusta/i, 'AGS'],
  [/birmingham/i, 'BHM'],
  [/memphis/i, 'MEM'],
  [/nashville/i, 'BNA'],

  // Southwest / West
  [/santa fe/i, 'SAF'],
  [/albuquerque/i, 'ABQ'],
  [/tucson/i, 'TUS'],
  [/phoenix/i, 'PHX'],
  [/scottsdale/i, 'SDL'],
  [/palm springs/i, 'PSP'],
  [/las vegas/i, 'LAS'],
  [/\blax\b/i, 'LAX'],
  [/los angeles/i, 'LAX'],
  [/\bsfo\b/i, 'SFO'],
  [/san francisco/i, 'SFO'],
  [/san jose/i, 'SJC'],
  [/oakland/i, 'OAK'],
  [/san diego/i, 'SAN'],
  [/carlsbad/i, 'CLD'],
  [/denver/i, 'DEN'],
  [/aspen/i, 'ASE'],
  [/vail/i, 'EGE'],
  [/eagle.*county/i, 'EGE'],
  [/seattle/i, 'SEA'],

  // Midwest
  [/columbus/i, 'CMH'],
  [/rickenbacker/i, 'LCK'],
  [/chicago.*exec/i, 'PWK'],
  [/o'?hare/i, 'ORD'],
  [/midway/i, 'MDW'],
  [/chicago/i, 'ORD'],
  [/dallas/i, 'DFW'],
  [/houston.*hobby/i, 'HOU'],
  [/houston/i, 'IAH'],
  [/minneapolis/i, 'MSP'],
  [/kansas city/i, 'MCI'],
  [/st\.?\s*louis/i, 'STL'],
  [/indianapolis/i, 'IND'],
  [/cincinnati/i, 'CVG'],
  [/louisville/i, 'SDF'],
  [/lexington/i, 'LEX'],
  [/des moines/i, 'DSM'],
  [/new orleans/i, 'MSY'],
  [/jackson.*ms/i, 'JAN'],

  // Georgia
  [/macon/i, 'MCN'],
  [/cobb county/i, 'RYY'],
  [/mccollum/i, 'RYY'],
  [/donaldson/i, 'GYH'],

  // Caribbean
  [/st\.?\s*thomas/i, 'STT'],
  [/\busvi\b/i, 'STT'],
  [/cyril.*king/i, 'STT'],
  [/virgin island/i, 'STT'],
  [/beef island/i, 'EIS'],
  [/tortola/i, 'EIS'],
  [/\btqpf\b/i, 'EIS'],
  [/\btupj\b/i, 'EIS'],
  [/san juan/i, 'SJU'],
  [/puerto rico/i, 'SJU'],
  [/st\.?\s*maarten/i, 'SXM'],
  [/sint maarten/i, 'SXM'],
  [/princess juliana/i, 'SXM'],
  [/st\.?\s*kitts/i, 'SKB'],
  [/antigua/i, 'ANU'],
  [/barbados/i, 'BGI'],
  [/trinidad/i, 'POS'],
  [/montego bay/i, 'MBJ'],
  [/jamaica/i, 'MBJ'],
  [/kingston.*jamaica/i, 'KIN'],
  [/nassau/i, 'NAS'],
  [/bahamas/i, 'NAS'],
  [/exuma/i, 'GGT'],
  [/great exuma/i, 'GGT'],

  // Europe
  [/\blfbg\b/i, 'LFBG'],
  [/le bourget/i, 'LBG'],
  [/bourget/i, 'LBG'],
  [/paris.*cdg/i, 'CDG'],
  [/charles de gaulle/i, 'CDG'],
  [/paris/i, 'LBG'],   // Default Paris to Le Bourget (private jet hub)
  [/luton/i, 'LTN'],
  [/stansted/i, 'STN'],
  [/gatwick/i, 'LGW'],
  [/heathrow/i, 'LHR'],
  [/farnborough/i, 'FAB'],
  [/london/i, 'LTN'],  // Default London to Luton (common for private jets)
  [/munich/i, 'MUC'],
  [/frankfurt/i, 'FRA'],
  [/amsterdam/i, 'AMS'],
  [/schiphol/i, 'AMS'],
  [/zurich/i, 'ZRH'],
  [/geneva/i, 'GVA'],
  [/nice/i, 'NCE'],
  [/madrid/i, 'MAD'],
  [/barcelona/i, 'BCN'],
  [/rome/i, 'FCO'],
  [/fiumicino/i, 'FCO'],
  [/milan/i, 'LIN'],
  [/vienna/i, 'VIE'],
  [/prague/i, 'PRG'],
  [/warsaw/i, 'WAW'],
  [/moscow/i, 'SVO'],
  [/tel aviv/i, 'TLV'],
  [/ben gurion/i, 'TLV'],
  [/israel/i, 'TLV'],

  // Middle East / Africa / Asia
  [/dubai/i, 'DXB'],
  [/riyadh/i, 'RUH'],
  [/jeddah/i, 'JED'],
  [/cairo/i, 'CAI'],
  [/johannesburg/i, 'JNB'],
  [/casablanca/i, 'CMN'],
  [/hong kong/i, 'HKG'],
  [/tokyo/i, 'HND'],
  [/singapore/i, 'SIN'],
  [/sydney/i, 'SYD'],
];

/**
 * Resolve a location string to coordinates.
 * Tries direct ICAO/IATA lookup first, then fuzzy name matching.
 */
function resolveCoords(location: string): [number, number] | null {
  if (!location) return null;
  const trimmed = location.trim();
  if (!trimmed) return null;

  // 1. Direct code lookup (e.g. "KTEB", "JFK", "TIST")
  const upper = trimmed.toUpperCase();
  if (AIRPORT_DB[upper]) return AIRPORT_DB[upper];

  // 2. Try extracting airport codes from strings like "LFBG Airport" or "(TEB)"
  const codeMatch = trimmed.match(/\b([A-Z]{3,4})\b/);
  if (codeMatch && AIRPORT_DB[codeMatch[1]]) {
    return AIRPORT_DB[codeMatch[1]];
  }

  // 3. Name pattern matching
  for (const [pattern, code] of NAME_PATTERNS) {
    if (pattern.test(trimmed)) {
      return AIRPORT_DB[code] || null;
    }
  }

  return null;
}

/**
 * Get a display label for an airport location.
 */
function getAirportLabel(location: string): string {
  if (!location) return 'Unknown';
  // Shorten long names
  return location.length > 30 ? location.slice(0, 28) + '...' : location;
}

/**
 * Get route color based on frequency.
 * 1 flight = blue, 2-5 = yellow/amber, 6+ = red
 */
function getRouteColor(count: number): string {
  if (count >= 6) return '#ef4444';   // red
  if (count >= 2) return '#f59e0b';   // amber
  return '#3b82f6';                   // blue
}

/**
 * Generate quadratic bezier arc points between two coordinates.
 * Curvature scales with great-circle distance.
 */
function generateArc(
  origin: [number, number],
  dest: [number, number],
  numPoints: number = 30
): [number, number][] {
  const midLat = (origin[0] + dest[0]) / 2;
  const midLng = (origin[1] + dest[1]) / 2;

  // Great-circle-ish distance for curvature scaling
  const dLat = origin[0] - dest[0];
  const dLng = origin[1] - dest[1];
  const dist = Math.sqrt(dLat * dLat + dLng * dLng);

  // Curvature perpendicular to the route — offset the control point
  // For east-west routes, offset latitude up; for north-south, offset longitude
  const angle = Math.atan2(dLat, dLng);
  const curvature = Math.min(dist * 0.2, 15); // Cap curvature for very long routes
  const controlLat = midLat + curvature * Math.cos(angle + Math.PI / 2);
  const controlLng = midLng - curvature * Math.sin(angle + Math.PI / 2);

  const points: [number, number][] = [];
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    const oneMinusT = 1 - t;
    const lat = oneMinusT * oneMinusT * origin[0] + 2 * oneMinusT * t * controlLat + t * t * dest[0];
    const lng = oneMinusT * oneMinusT * origin[1] + 2 * oneMinusT * t * controlLng + t * t * dest[1];
    points.push([lat, lng]);
  }
  return points;
}

// ---------------------------------------------------------------------------
// Route aggregation type
// ---------------------------------------------------------------------------
interface AggregatedRoute {
  originCoords: [number, number];
  destCoords: [number, number];
  originLabel: string;
  destLabel: string;
  count: number;
  flightIds: string[];
  passengers: Set<string>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function FlightMap({ onSelectFlight, filters }: FlightMapProps) {
  const [passenger, setPassenger] = useState('');
  const [year, setYear] = useState('');
  const [page, setPage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedRouteKey, setSelectedRouteKey] = useState<string | null>(null);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const routeLayerRef = useRef<any>(null);
  const markerLayerRef = useRef<any>(null);

  const { data, isLoading } = useFlights({
    passenger: passenger || undefined,
    year: year || undefined,
    page,
    per_page: 200,
  });

  const flights = data?.data || [];
  const meta = data?.meta;

  // Aggregate routes
  const { routes, airports } = useMemo(() => {
    const routeMap = new Map<string, AggregatedRoute>();
    const airportSet = new Map<string, { coords: [number, number]; label: string; count: number }>();

    for (const flight of flights) {
      const oCoords = resolveCoords(flight.origin);
      const dCoords = resolveCoords(flight.destination);
      if (!oCoords || !dCoords) continue;

      // Route key: sorted coords to merge A->B and B->A visually
      const keyParts = [
        `${oCoords[0].toFixed(4)},${oCoords[1].toFixed(4)}`,
        `${dCoords[0].toFixed(4)},${dCoords[1].toFixed(4)}`,
      ].sort();
      const key = keyParts.join('|');

      const existing = routeMap.get(key);
      if (existing) {
        existing.count++;
        existing.flightIds.push(flight.id);
        flight.passenger_names.forEach((n: string) => existing.passengers.add(n));
      } else {
        routeMap.set(key, {
          originCoords: oCoords,
          destCoords: dCoords,
          originLabel: flight.origin,
          destLabel: flight.destination,
          count: 1,
          flightIds: [flight.id],
          passengers: new Set(flight.passenger_names),
        });
      }

      // Track airports
      const oKey = `${oCoords[0].toFixed(4)},${oCoords[1].toFixed(4)}`;
      const dKey = `${dCoords[0].toFixed(4)},${dCoords[1].toFixed(4)}`;
      if (!airportSet.has(oKey)) {
        airportSet.set(oKey, { coords: oCoords, label: flight.origin, count: 0 });
      }
      airportSet.get(oKey)!.count++;
      if (!airportSet.has(dKey)) {
        airportSet.set(dKey, { coords: dCoords, label: flight.destination, count: 0 });
      }
      airportSet.get(dKey)!.count++;
    }

    return {
      routes: Array.from(routeMap.entries()),
      airports: Array.from(airportSet.values()),
    };
  }, [flights]);

  // Stats
  const resolvedCount = useMemo(() => {
    let count = 0;
    for (const flight of flights) {
      if (resolveCoords(flight.origin) && resolveCoords(flight.destination)) count++;
    }
    return count;
  }, [flights]);

  // Initialize Leaflet map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    let cancelled = false;

    import('leaflet').then((L) => {
      if (cancelled || !mapRef.current) return;

      // Fix default icon paths for bundlers
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      const map = L.map(mapRef.current!, {
        center: [28, -50],
        zoom: 3,
        zoomControl: false,          // Disable default (top-left) zoom
        attributionControl: false,
        minZoom: 2,
        maxZoom: 18,
        worldCopyJump: true,
      });

      // Add zoom control to bottom-right so it doesn't overlap toolbar
      L.control.zoom({ position: 'bottomright' }).addTo(map);

      // Dark tile layer
      // Inside Discord Activity: patchUrlMappings rewrites the URL through the proxy
      // Standalone: use direct URL
      const tileUrl = isEmbedded
        ? 'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png'
        : 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png';
      L.tileLayer(tileUrl, {
        maxZoom: 19,
        subdomains: isEmbedded ? 'a' : 'abcd',
        attribution: '&copy; CARTO',
      }).addTo(map);

      routeLayerRef.current = L.layerGroup().addTo(map);
      markerLayerRef.current = L.layerGroup().addTo(map);
      mapInstanceRef.current = map;

      // Invalidate size multiple times to handle layout settling
      setTimeout(() => map.invalidateSize(), 100);
      setTimeout(() => map.invalidateSize(), 500);
      setTimeout(() => map.invalidateSize(), 1500);
    });

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        routeLayerRef.current = null;
        markerLayerRef.current = null;
      }
    };
  }, []);

  // Draw routes and markers
  useEffect(() => {
    if (!routeLayerRef.current || !markerLayerRef.current) return;

    import('leaflet').then((L) => {
      const routeLayer = routeLayerRef.current;
      const markerLayer = markerLayerRef.current;
      if (!routeLayer || !markerLayer) return;

      routeLayer.clearLayers();
      markerLayer.clearLayers();

      // Draw route arcs
      for (const [key, route] of routes) {
        const arcPoints = generateArc(route.originCoords, route.destCoords);
        const color = getRouteColor(route.count);
        const weight = Math.min(1.5 + Math.log2(route.count) * 0.8, 5);
        const opacity = Math.min(0.4 + route.count * 0.06, 0.95);

        const polyline = L.polyline(arcPoints, {
          color,
          weight,
          opacity,
          smoothFactor: 1.5,
          dashArray: '8 4',           // Animated dashed lines
          lineCap: 'round',
          lineJoin: 'round',
          className: 'flight-route-line',
        });

        const topPassengers = Array.from(route.passengers).slice(0, 5);
        const tooltipHtml = [
          `<strong>${getAirportLabel(route.originLabel)}</strong>`,
          `<strong>${getAirportLabel(route.destLabel)}</strong>`,
          `<br>${route.count} flight${route.count !== 1 ? 's' : ''}`,
          topPassengers.length > 0 ? `<br><span style="opacity:0.7">${topPassengers.join(', ')}${route.passengers.size > 5 ? ` +${route.passengers.size - 5}` : ''}</span>` : '',
        ].join(' &rarr; ');

        polyline.bindTooltip(tooltipHtml, {
          sticky: true,
          direction: 'top',
          className: 'flight-route-tooltip',
        });

        polyline.on('click', () => {
          setSelectedRouteKey(key);
          onSelectFlight(route.flightIds[0]);
        });

        polyline.on('mouseover', function (this: any) {
          this.setStyle({ weight: weight + 2, opacity: 1 });
        });

        polyline.on('mouseout', function (this: any) {
          this.setStyle({ weight, opacity });
        });

        routeLayer.addLayer(polyline);
      }

      // Draw airport markers
      for (const airport of airports) {
        const radius = Math.min(3 + Math.log2(airport.count + 1) * 2, 10);

        const marker = L.circleMarker(airport.coords, {
          radius,
          fillColor: '#ffffff',
          fillOpacity: 0.9,
          color: '#3b82f6',
          weight: 2,
        });

        marker.bindPopup(
          `<div style="font-size:13px;min-width:120px">` +
          `<strong>${getAirportLabel(airport.label)}</strong><br>` +
          `<span style="color:#999">${airport.count} flight${airport.count !== 1 ? 's' : ''}</span>` +
          `</div>`,
          { className: 'flight-airport-popup' }
        );

        marker.bindTooltip(getAirportLabel(airport.label), {
          direction: 'top',
          offset: [0, -radius],
          className: 'flight-airport-tooltip',
        });

        markerLayer.addLayer(marker);
      }
    });
  }, [routes, airports, onSelectFlight]);

  // Handle resize when sidebar toggles
  useEffect(() => {
    const timer = setTimeout(() => {
      mapInstanceRef.current?.invalidateSize();
    }, 300);
    return () => clearTimeout(timer);
  }, [sidebarOpen]);

  const handlePassengerChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPassenger(e.target.value);
    setPage(1);
  }, []);

  const handleYearChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setYear(e.target.value);
    setPage(1);
  }, []);

  const years = useMemo(() => Array.from({ length: 30 }, (_, i) => String(1995 + i)), []);

  return (
    <div className="flight-map-container">
      {/* Left sidebar - flight list */}
      <div className={`flight-sidebar ${sidebarOpen ? 'flight-sidebar--open' : 'flight-sidebar--closed'}`}>
        <div className="flight-sidebar-header">
          <div className="flight-sidebar-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.4-.1.9.3 1.1l5.5 3.2-2.9 2.9L4 14.2c-.3-.1-.7 0-.9.2l-.2.3c-.2.3-.1.7.1.9l3.3 2.1 2.1 3.3c.2.3.6.4.9.1l.3-.2c.3-.2.3-.6.2-.9l-.2-2.3 2.9-2.9 3.2 5.5c.2.4.7.5 1.1.3l.5-.3c.4-.2.6-.6.5-1.1z"/>
            </svg>
            <span>Flights</span>
          </div>
          <button
            className="flight-sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {sidebarOpen
                ? <path d="M15 18l-6-6 6-6" />
                : <path d="M9 18l6-6-6-6" />
              }
            </svg>
          </button>
        </div>

        {sidebarOpen && (
          <>
            <div className="flight-sidebar-filters">
              <input
                type="text"
                className="flight-filter-input"
                placeholder="Filter by passenger..."
                value={passenger}
                onChange={handlePassengerChange}
              />
              <select
                className="flight-filter-select"
                value={year}
                onChange={handleYearChange}
              >
                <option value="">All years</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <div className="flight-sidebar-stats">
              {isLoading ? (
                <span>Loading flights...</span>
              ) : (
                <>
                  <span>{meta?.total?.toLocaleString() ?? 0} flights</span>
                  <span className="flight-sidebar-stats-sep">&middot;</span>
                  <span>{resolvedCount} mapped</span>
                  <span className="flight-sidebar-stats-sep">&middot;</span>
                  <span>{routes.length} routes</span>
                </>
              )}
            </div>

            <div className="flight-sidebar-list">
              {flights.length === 0 && !isLoading && (
                <div className="flight-sidebar-empty">
                  No flights found matching your filters.
                </div>
              )}
              {flights.map((flight) => {
                const hasCoords = resolveCoords(flight.origin) && resolveCoords(flight.destination);
                return (
                  <button
                    key={flight.id}
                    className={`flight-list-item ${hasCoords ? '' : 'flight-list-item--unmapped'}`}
                    onClick={() => onSelectFlight(flight.id)}
                  >
                    <div className="flight-route-label">
                      <span className="flight-origin">{getAirportLabel(flight.origin)}</span>
                      <svg className="flight-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                      <span className="flight-dest">{getAirportLabel(flight.destination)}</span>
                    </div>
                    <div className="flight-meta">
                      <span className="flight-date">{flight.date}</span>
                      {flight.aircraft && (
                        <>
                          <span className="flight-meta-sep">&middot;</span>
                          <span>{flight.aircraft}</span>
                        </>
                      )}
                      <span className="flight-meta-sep">&middot;</span>
                      <span>{flight.passenger_count} pax</span>
                    </div>
                    {flight.passenger_names.length > 0 && (
                      <div className="flight-passengers-list">
                        {flight.passenger_names.slice(0, 4).join(', ')}
                        {flight.passenger_names.length > 4 && (
                          <span className="flight-more"> +{flight.passenger_names.length - 4}</span>
                        )}
                      </div>
                    )}
                    {!hasCoords && (
                      <div className="flight-unmapped-badge">coordinates unknown</div>
                    )}
                  </button>
                );
              })}
            </div>

            {meta && (
              <div className="flight-sidebar-pager">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  &larr; Prev
                </button>
                <span className="flight-page-info">
                  Page {page}{meta.total ? ` of ${Math.ceil(meta.total / 200)}` : ''}
                </span>
                <button
                  disabled={!meta.has_more}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next &rarr;
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Map area */}
      <div className="flight-map-area">
        {/* Toolbar overlay on top of map */}
        <div className="flight-map-toolbar">
          <div className="flight-map-legend">
            <span className="flight-legend-item">
              <span className="flight-legend-dot" style={{ background: '#3b82f6' }} />
              1 flight
            </span>
            <span className="flight-legend-item">
              <span className="flight-legend-dot" style={{ background: '#f59e0b' }} />
              2-5
            </span>
            <span className="flight-legend-item">
              <span className="flight-legend-dot" style={{ background: '#ef4444' }} />
              6+
            </span>
          </div>
        </div>

        <div ref={mapRef} className="flight-map" />
      </div>
    </div>
  );
}
